import axios from 'axios';

// Server origin — no /api/v1 suffix; each route includes it explicitly
const _rawUrl = process.env.REACT_APP_API_URL || 'http://localhost:9000';
const API_BASE = _rawUrl.replace(/\/api\/v1\/?$/, '').replace(/\/+$/, '');

const api = axios.create({ baseURL: API_BASE });

// Attach JWT to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Redirect to login on 401; normalise Pydantic v2 error arrays into a plain string
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      ['access_token', 'user_role', 'operator_id', 'location_id', 'vendor_id', 'vendor_name']
        .forEach(k => localStorage.removeItem(k));
      window.location.href = '/login';
    }
    // Pydantic v2 validation errors come as detail: [{type, loc, msg, input, ctx}, ...]
    // Flatten into a readable string so any page can safely render err.message
    const detail = err.response?.data?.detail;
    if (Array.isArray(detail)) {
      err.message = detail.map(d => {
        const field = d.loc ? d.loc.slice(1).join('.') : '';
        return field ? `${field}: ${d.msg}` : d.msg;
      }).join('; ');
      // Also overwrite detail so existing code using .detail still gets a string
      err.response.data.detail = err.message;
    }
    return Promise.reject(err);
  }
);

// ------------------------------------------------------------------ //
// Auth
// ------------------------------------------------------------------ //

export async function login(username, password) {
  const form = new URLSearchParams({ username, password });
  const { data } = await api.post('/api/v1/auth/login', form, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
  localStorage.setItem('user_role', data.role || 'administrator');
  localStorage.removeItem('operator_id');
  localStorage.removeItem('location_id');
  if (data.vendor_id)   localStorage.setItem('vendor_id',   data.vendor_id);
  else                  localStorage.removeItem('vendor_id');
  if (data.vendor_name) localStorage.setItem('vendor_name', data.vendor_name);
  else                  localStorage.removeItem('vendor_name');
  return data;
}

export async function supervisorLogin(employee_id, password) {
  const { data } = await api.post('/api/v1/auth/supervisor-login', { employee_id, password });
  localStorage.setItem('user_role',    data.role        || 'supervisor');
  localStorage.setItem('operator_id',  data.operator_id || '');
  localStorage.setItem('location_id',  data.location_id || '');
  if (data.vendor_id) localStorage.setItem('vendor_id', data.vendor_id);
  return data;
}

// ------------------------------------------------------------------ //
// Role helpers (read from localStorage — set at login time)
// ------------------------------------------------------------------ //

export function getUserRole() {
  // Older/local sessions may contain only the JWT. Fall back to its claims so
  // navigation does not silently collapse to the viewer menu after a restart
  // or upgrade.
  return getTokenClaims()?.role || localStorage.getItem('user_role') || 'viewer';
}

/** role === "super_administrator" */
export function isSuperAdmin() {
  return getUserRole() === 'super_administrator';
}

/** role === "administrator" AND has vendor_id (vendor-admin) */
export function isVendorAdmin() {
  return getUserRole() === 'administrator' && !!getVendorId();
}

/** role === "administrator" AND no vendor_id (infra-admin / env-var) */
export function isInfraAdmin() {
  return getUserRole() === 'administrator' && !getVendorId();
}

/** True for any administrator role (vendor OR infra) */
export function isAdmin() {
  return getUserRole() === 'administrator';
}

export function isSupervisor() {
  return getUserRole() === 'supervisor';
}

export function getOperatorId() {
  return localStorage.getItem('operator_id') || null;
}

export function getLocationId() {
  return localStorage.getItem('location_id') || null;
}

export function getVendorId() {
  return getTokenClaims()?.vendor_id || localStorage.getItem('vendor_id') || null;
}

export function getVendorName() {
  return getTokenClaims()?.vendor_name || localStorage.getItem('vendor_name') || null;
}

/** Parse JWT claims without a library (display/scoping convenience only). */
function getTokenClaims() {
  const token = localStorage.getItem('access_token');
  if (!token) return null;
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
}

/** Backward-compatible role helper used by older components. */
export function getRoleFromToken() {
  return getTokenClaims()?.role || null;
}

// ------------------------------------------------------------------ //
// Devices
// ------------------------------------------------------------------ //

export const getDevices      = ()         => api.get('/api/v1/devices').then(r => r.data);
export const getDevice       = (id)       => api.get(`/api/v1/devices/${id}`).then(r => r.data);
export const registerDevice  = (payload)  => api.post('/api/v1/admin/devices', payload).then(r => r.data);
export const updateDevice    = (id, data) => api.put(`/api/v1/admin/devices/${id}`, data).then(r => r.data);

// ------------------------------------------------------------------ //
// Events
// ------------------------------------------------------------------ //

export const getEvents = (params) => api.get('/api/v1/events', { params }).then(r => r.data);
export const getEventImage = (imageRef) =>
  `${API_BASE}/api/v1/images/${imageRef}?token=${localStorage.getItem('access_token')}`;

// ------------------------------------------------------------------ //
// Operators
// ------------------------------------------------------------------ //

export const getOperators  = (params)       => api.get('/api/v1/operators',          { params }).then(r => r.data);
export const getOperator   = (id)           => api.get(`/api/v1/operators/${id}`).then(r => r.data);
export const createOperator = (data)        => api.post('/api/v1/operators', data).then(r => r.data);
export const updateOperator = (id, data)    => api.put(`/api/v1/operators/${id}`, data).then(r => r.data);
export const deleteOperator = (id)          => api.delete(`/api/v1/operators/${id}`).then(r => r.data);
export const resetPin       = (id, new_pin) => api.post(`/api/v1/operators/${id}/reset-pin`, { new_pin }).then(r => r.data);
export const setDashboardPassword = (id, password) =>
  api.post(`/api/v1/operators/${id}/set-dashboard-password`, { password }).then(r => r.data);

// ------------------------------------------------------------------ //
// Sessions
// ------------------------------------------------------------------ //

export const getActiveSessions = (tz = 'UTC') =>
  api.get('/api/v1/sessions/active', { params: { tz } }).then(r => r.data);

export const getSessions = (params) =>
  api.get('/api/v1/sessions', { params }).then(r => r.data);

export const forceLogout = (session_id, reason) =>
  api.post(`/api/v1/sessions/${session_id}/force-logout`, { reason }).then(r => r.data);

// ------------------------------------------------------------------ //
// Breaks
// ------------------------------------------------------------------ //

export const getPendingBreaks = () =>
  api.get('/api/v1/breaks/pending').then(r => r.data);

export const approveBreak = (break_id, passkey) =>
  api.post(`/api/v1/breaks/${break_id}/approve`, { passkey }).then(r => r.data);

export const rejectBreak = (break_id, passkey, reason) =>
  api.post(`/api/v1/breaks/${break_id}/reject`, { passkey, reason }).then(r => r.data);

// ------------------------------------------------------------------ //
// Locations
// ------------------------------------------------------------------ //

export const getLocations    = ()          => api.get('/api/v1/locations').then(r => r.data);
export const createLocation  = (data)      => api.post('/api/v1/locations', data).then(r => r.data);
export const updateLocation  = (id, data)  => api.put(`/api/v1/locations/${id}`, data).then(r => r.data);
export const deleteLocation  = (id)        => api.delete(`/api/v1/locations/${id}`).then(r => r.data);

// ------------------------------------------------------------------ //
// Departments & Shifts
// ------------------------------------------------------------------ //

export const getDepartments  = () => api.get('/api/v1/departments').then(r => r.data);
export const createDepartment = (d) => api.post('/api/v1/departments', d).then(r => r.data);
export const getShifts       = (params) => api.get('/api/v1/shifts', { params }).then(r => r.data);
export const getShift        = (id) => api.get(`/api/v1/shifts/${id}`).then(r => r.data);
export const createShift     = (d) => api.post('/api/v1/shifts', d).then(r => r.data);
export const updateShift     = (id, d) => api.put(`/api/v1/shifts/${id}`, d).then(r => r.data);
export const deleteShift     = (id) => api.delete(`/api/v1/shifts/${id}`).then(r => r.data);
export const assignOperatorShift = (id, shift_id) =>
  api.put(`/api/v1/operators/${id}/shift`, { shift_id }).then(r => r.data);
export const bulkAssignShift = (shift_id, operator_ids) =>
  api.post('/api/v1/operators/assign-shift', { shift_id, operator_ids }).then(r => r.data);

// ------------------------------------------------------------------ //
// Reports
// ------------------------------------------------------------------ //

export const getAttendanceReport  = (p) => api.get('/api/v1/reports/attendance',  { params: p }).then(r => r.data);
export const getDrowsinessReport  = (p) => api.get('/api/v1/reports/drowsiness',  { params: p }).then(r => r.data);
export const getShiftUtilizationReport = (p) =>
  api.get('/api/v1/reports/shift-utilization', { params: p }).then(r => r.data);
export const getDetectionSettings = () => api.get('/api/v1/supervisor/detection-settings').then(r => r.data);
export const getPresenceSummaries = (params = {}) =>
  api.get('/api/v1/presence/summaries', { params }).then(r => r.data);
export const getAwaySessions = (summaryId) =>
  api.get(`/api/v1/presence/summaries/${summaryId}/away-sessions`).then(r => r.data);
export const correctPresence = (summaryId, patch) =>
  api.patch(`/api/v1/presence/${summaryId}`, patch).then(r => r.data);
export const getPresenceCorrections = (summaryId) =>
  api.get(`/api/v1/presence/${summaryId}/corrections`).then(r => r.data);
export const addAwaySession = (summaryId, patch) =>
  api.post(`/api/v1/presence/${summaryId}/away-sessions`, patch).then(r => r.data);
export const updateAwaySession = (awayId, patch) =>
  api.patch(`/api/v1/presence/away-sessions/${awayId}`, patch).then(r => r.data);
export const deleteAwaySession = (awayId, reason) =>
  api.delete(`/api/v1/presence/away-sessions/${awayId}`, { data: { reason } }).then(r => r.data);

// ------------------------------------------------------------------ //
// Audit
// ------------------------------------------------------------------ //

export const getAuditLogs = (params) => api.get('/api/v1/audit', { params }).then(r => r.data);

// ------------------------------------------------------------------ //
// Vendors (super-admin only)
// ------------------------------------------------------------------ //

export const getVendors = () => api.get('/api/v1/vendors').then(r => r.data);
export const getVendorFeatures = (id) => api.get(`/api/v1/superadmin/vendors/${id}/features`).then(r => r.data);
export const updateVendorFeatures = (id, data) => api.put(`/api/v1/superadmin/vendors/${id}/features`, data).then(r => r.data);
export const createVendor = (data) => api.post('/api/v1/vendors', data).then(r => r.data);
export const updateVendor = (id, data) => api.put(`/api/v1/vendors/${id}`, data).then(r => r.data);
export const resetVendorAdminPassword = (id, new_password) =>
  api.put(`/api/v1/vendors/${id}/admin/reset-password`, { new_password }).then(r => r.data);

// Enrollment password management (vendor-admin only, own vendor)
export const getEnrollmentPasswordStatus = (vendorId) =>
  api.get(`/api/v1/vendors/${vendorId}/enrollment-password`).then(r => r.data);
export const setEnrollmentPassword = (vendorId, password, confirmPassword) =>
  api.post(`/api/v1/vendors/${vendorId}/enrollment-password`, { password, confirm_password: confirmPassword }).then(r => r.data);
export const clearEnrollmentPassword = (vendorId) =>
  api.delete(`/api/v1/vendors/${vendorId}/enrollment-password`).then(r => r.data);

// ------------------------------------------------------------------ //
// Retention
// ------------------------------------------------------------------ //

export const getRetentionPolicies  = ()           => api.get('/api/v1/retention').then(r => r.data);
export const updateRetentionPolicy = (target, d)  => api.put(`/api/v1/retention/${target}`, d).then(r => r.data);
export const runRetention          = ()           => api.post('/api/v1/retention/run').then(r => r.data);

// ------------------------------------------------------------------ //
// Settings
// ------------------------------------------------------------------ //

export const getTimezone   = () => api.get('/api/v1/settings/timezone').then(r => r.data);
export const setTimezone   = (tz) => api.put('/api/v1/settings/timezone', { timezone: tz }).then(r => r.data);
export const getTimezones  = () => api.get('/api/v1/settings/timezones').then(r => r.data);

export default api;

// ------------------------------------------------------------------ //
// Missing exports that other files depend on
// ------------------------------------------------------------------ //

// Base URL used by useSSE hook for EventSource (no /api/v1 suffix)
// BASE_URL = server origin without /api/v1 (used by useSSE EventSource)
export const BASE_URL = _rawUrl.replace(/\/api\/v1\/?$/, '');

// Image URL with JWT token as query param (img src can't send headers)
export function getImageUrl(imageRef) {
  if (!imageRef) return null;
  const token = localStorage.getItem('access_token');
  return `${API_BASE}/api/v1/images/${imageRef}?token=${encodeURIComponent(token)}`;
}

// MJPEG stream URL for a device (via server proxy)
export function getStreamUrl(deviceId) {
  const token = localStorage.getItem('access_token');
  return `${API_BASE}/api/v1/devices/${deviceId}/stream?token=${encodeURIComponent(token)}`;
}

// Display timezone is admin-configured and DB-backed (see getTimezone/
// setTimezone above + hooks/useTimezone.js) — no longer a per-browser
// localStorage preference.

// Alias: old name was listTimezones, new name is getTimezones
export const listTimezones = getTimezones;

// Dedicated supervisors fetch — returns only active supervisors for dropdowns
export const getSupervisors = () =>
  api.get('/api/v1/operators', { params: { status: 'active', page_size: 200 } })
    .then(r => (r.data.items || []).filter(o => o.role === 'supervisor'));

export const deleteDeviceOnly = (id) => api.delete(`/api/v1/admin/devices/${id}`).then(r => r.data);
export const getDeviceDependencies = (id) =>
  api.get(`/api/v1/admin/devices/${id}/dependencies`).then(r => r.data);
export const cleanupDevice = (id) =>
  api.post(`/api/v1/admin/devices/${id}/cleanup`).then(r => r.data);

export const validateEvent = (eventId, isValid) =>
  api.post(`/api/v1/events/${eventId}/validate`, { is_valid: isValid }).then(r => r.data);

export const buzzEventAlarm = (eventId) =>
  api.post(`/api/v1/events/${eventId}/buzz`).then(r => r.data);

export const requestDeviceSetup = (id) => api.post(`/api/v1/admin/devices/${id}/request-setup`).then(r => r.data);

// ------------------------------------------------------------------ //
// Alarm Configuration
// ------------------------------------------------------------------ //

export const getAlarmConfig = () => api.get('/api/v1/alarm-config').then(r => r.data);
export const getAlarmConfigVersions = () => api.get('/api/v1/alarm-config/version').then(r => r.data);
export const updateAlarmConfig = (feature, data) =>
  api.put(`/api/v1/alarm-config/${feature}`, data).then(r => r.data);
export const restoreDefaultAlarmConfig = (feature) =>
  api.post(`/api/v1/alarm-config/${feature}/restore-default`).then(r => r.data);

// Device Assignment ("Supervisor-owned deployment") now covers all 6
// features (drowsiness/yawning/distracted/presence/screenshot/
// browser_history) via /feature-config — not just alarm sound. See
// server/services/feature_config.py.
export const getFeatureAssignments = (feature) =>
  api.get(`/api/v1/feature-config/${feature}/assignments`).then(r => r.data);
export const setFeatureAssignments = (feature, deviceIds) =>
  api.put(`/api/v1/feature-config/${feature}/assignments`, { device_ids: deviceIds }).then(r => r.data);
export const getEffectiveFeatureConfig = (deviceId) =>
  api.get('/api/v1/feature-config/effective', { params: { device_id: deviceId } }).then(r => r.data);

export const uploadAlarmAudio = (feature, file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post(`/api/v1/alarm-config/${feature}/audio`, form).then(r => r.data);
};
export const deleteAlarmAudio = (feature) =>
  api.delete(`/api/v1/alarm-config/${feature}/audio`).then(r => r.data);
export const getAlarmAudioHistory = (feature) =>
  api.get(`/api/v1/alarm-config/${feature}/audio/history`).then(r => r.data);
export const restoreAlarmAudioVersion = (feature, audioFileId) =>
  api.post(`/api/v1/alarm-config/${feature}/audio/restore/${audioFileId}`).then(r => r.data);

// Preview <audio> playback — token in query param, same reasoning as
// getImageUrl/getStreamUrl (native media tags can't send Authorization headers).
export const getAlarmAudioUrl = (feature) => {
  const token = localStorage.getItem('access_token');
  return `${API_BASE}/api/v1/alarm-config/${feature}/audio?token=${encodeURIComponent(token)}`;
};

export const testAlarm = (feature, deviceId) =>
  api.post(`/api/v1/alarm-config/${feature}/test`, { device_id: deviceId || null }).then(r => r.data);
export const getAlarmStats = (deviceId) =>
  api.get(`/api/v1/alarm-config/stats/${deviceId}`).then(r => r.data);

// ------------------------------------------------------------------ //
// Work Activity Monitoring
// ------------------------------------------------------------------ //

export const getActivityPolicy = (locationId) =>
  api.get('/api/v1/activity/policies', { params: locationId ? { location_id: locationId } : {} })
     .then(r => r.data);
export const updateActivityPolicy = (patch, locationId) =>
  api.put('/api/v1/activity/policies', patch, { params: locationId ? { location_id: locationId } : {} })
     .then(r => r.data);

export const getActivityStatus = (deviceId) =>
  api.get('/api/v1/activity/status', { params: { device_id: deviceId } }).then(r => r.data);
export const triggerActivityAction = (deviceId, action) =>
  api.post('/api/v1/activity/actions', { device_id: deviceId, action }).then(r => r.data);
export const getActivityActionStatus = (requestId) =>
  api.get(`/api/v1/activity/actions/${requestId}`).then(r => r.data);

export const getActivityScreenshots = (params) =>
  api.get('/api/v1/activity/screenshots', { params }).then(r => r.data);
export const getActivityBrowserHistory = (params) =>
  api.get('/api/v1/activity/browser-history', { params }).then(r => r.data);
export const getActivityBrowserHistoryEntries = (historyId) =>
  api.get(`/api/v1/activity/browser-history/${historyId}/entries`).then(r => r.data);

// <img>/token-in-query pattern — same reasoning as getEventImage/getAlarmAudioUrl.
export const getActivityScreenshotUrl = (screenshotId) => {
  const token = localStorage.getItem('access_token');
  return `${API_BASE}/api/v1/activity/screenshots/${screenshotId}/image?token=${encodeURIComponent(token)}`;
};
export const deleteActivityScreenshot = (screenshotId) =>
  api.delete(`/api/v1/activity/screenshots/${screenshotId}`).then(r => r.data);
